import { inject, Injectable } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { environment } from '../../../environments/environment';
import { logOktaDebug, serializeUnknownError } from '../auth/okta-debug-log';
import { eventqOktaRedirectUriFromEnv } from '../auth/okta-redirect';

/**
 * Thin EventQ wrapper around `@okta/okta-angular` / `@okta/okta-auth-js`.
 * After redirect, {@link handleRedirectCallback} returns the Okta **ID token**
 * for STS `Auth/ExchangeEventq` (`subject_token_type=id_token`).
 */
@Injectable({ providedIn: 'root' })
export class OktaAuthService {
  private readonly oktaAuth = inject(OKTA_AUTH);
  private tokenExchangePromise: Promise<string> | null = null;

  isConfigured(): boolean {
    const configured = Boolean(
      environment.okta.issuer?.trim() &&
        environment.okta.clientId?.trim() &&
        eventqOktaRedirectUriFromEnv(),
    );
    logOktaDebug('OktaAuthService.isConfigured', {
      configured,
      sdkRedirectUri: this.oktaAuth.options?.redirectUri,
    });
    return configured;
  }

  async loginWithRedirect(): Promise<void> {
    logOktaDebug('OktaAuthService.loginWithRedirect: enter');
    if (!this.isConfigured()) {
      logOktaDebug('OktaAuthService.loginWithRedirect: abort not configured');
      throw new Error('Okta is not configured. Set issuer and clientId in environment.');
    }

    console.info('[Okta] starting redirect', {
      issuer: environment.okta.issuer,
      clientId: environment.okta.clientId,
      redirectUri: eventqOktaRedirectUriFromEnv(),
    });
    logOktaDebug('OktaAuthService.loginWithRedirect: calling signInWithRedirect', {
      sdkOptions: {
        issuer: this.oktaAuth.options?.issuer,
        clientId: this.oktaAuth.options?.clientId,
        redirectUri: this.oktaAuth.options?.redirectUri,
        scopes: this.oktaAuth.options?.scopes,
      },
    });

    try {
      await this.oktaAuth.signInWithRedirect();
      logOktaDebug('OktaAuthService.loginWithRedirect: signInWithRedirect resolved');
    } catch (error: unknown) {
      logOktaDebug('OktaAuthService.loginWithRedirect: signInWithRedirect rejected', {
        error: serializeUnknownError(error),
      });
      throw error;
    }
  }

  /**
   * Completes the Okta redirect and returns the Okta ID token.
   */
  async handleRedirectCallback(): Promise<string> {
    logOktaDebug('OktaAuthService.handleRedirectCallback: enter', {
      alreadyInFlight: Boolean(this.tokenExchangePromise),
    });
    if (!this.isConfigured()) {
      logOktaDebug('OktaAuthService.handleRedirectCallback: abort not configured');
      throw new Error('Okta is not configured. Set issuer and clientId in environment.');
    }

    if (this.tokenExchangePromise) {
      logOktaDebug('OktaAuthService.handleRedirectCallback: reusing in-flight promise');
      return this.tokenExchangePromise;
    }

    this.tokenExchangePromise = this.completeRedirect();
    try {
      return await this.tokenExchangePromise;
    } finally {
      this.tokenExchangePromise = null;
    }
  }

  private async completeRedirect(): Promise<string> {
    logOktaDebug('OktaAuthService.completeRedirect: calling storeTokensFromRedirect');
    try {
      // Do not call handleLoginRedirect — it restores originalUri (`/login`) and
      // destroys this callback before STS can establish the EventQ session.
      await this.oktaAuth.storeTokensFromRedirect();
      logOktaDebug('OktaAuthService.completeRedirect: storeTokensFromRedirect resolved');
    } catch (error: unknown) {
      console.error('[Okta] login callback failed', error);
      logOktaDebug('OktaAuthService.completeRedirect: storeTokensFromRedirect rejected', {
        error: serializeUnknownError(error),
      });
      throw error;
    }

    const idToken = this.oktaAuth.getIdToken();
    logOktaDebug('OktaAuthService.completeRedirect: getIdToken', {
      present: Boolean(idToken),
      length: idToken?.length ?? 0,
    });
    if (!idToken) {
      throw new Error('Okta login failed: no ID token returned.');
    }

    console.info('[Okta] ID token received', {
      present: true,
      length: idToken.length,
    });

    return idToken;
  }
}
